import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  memo,
} from "react";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/freeCustomerList.scss";

const WEEK = ["日", "月", "火", "水", "木", "金", "土"];
const WEEK_EN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const ROWS_PER_PAGE = 80;

const MEMBER_LABELS = {
  joined: { en: "Joined", ja: "入会済", className: "mb-joined" },
  existing: { en: "Existing", ja: "既会員", className: "mb-existing" },
  former: { en: "Former", ja: "退会済", className: "mb-former" },
  none: { en: "No Match", ja: "未入会", className: "mb-none" },
};

// ----- 一覧の列定義 (見出しの描画とソートに使う) -----
// type: 比較のしかた / dir: その列を最初にクリックしたときの向き
const COLUMNS = [
  { key: "id", title: "ID", type: "number", dir: "desc" },
  { key: "member_status", title: "会員", type: "member", dir: "asc" },
  { key: "name", title: "名前", type: "text", dir: "asc" },
  { key: "age", title: "年齢", type: "number", dir: "asc" },
  { key: "date", title: "予約日", type: "text", dir: "desc" },
  { key: "studio", title: "店舗", type: "text", dir: "asc" },
  { key: "lesson", title: "レッスン", type: "text", dir: "asc" },
  { key: "created_at", title: "申込日", type: "text", dir: "desc" },
  { key: "mail", title: "メール", type: "text", dir: "asc" },
  { key: "postcode", title: "郵便", type: "text", dir: "asc" },
  { key: "tel", title: "電話", type: "text", dir: "asc" },
  { key: "access", title: "交通手段", type: "text", dir: "asc" },
  { key: "message", title: "メッセージ", type: "text", dir: "asc" },
  { key: null, title: "編集", className: "center" },
];

// 会員ステータスの並び順 (入会済 → 既会員 → 退会済 → 未入会)
const MEMBER_ORDER = { joined: 0, existing: 1, former: 2, none: 3 };

// 値が無い行は昇順・降順どちらでも末尾に置くため、先に判定する
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

// 年齢は文字列カラム ("12" 等) なので数値化する。
// 空欄や数値にできない値 ("小4" 等) は null にして末尾へ送る
const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const raw = String(v).trim();
  if (raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const compareValues = (a, b, type) => {
  if (type === "number") return a - b;
  if (type === "member") {
    return (MEMBER_ORDER[a] ?? 99) - (MEMBER_ORDER[b] ?? 99);
  }
  // 日付は YYYY-MM-DD なので文字列比較で日付順になる
  return String(a).localeCompare(String(b), "ja");
};

// 見出しをクリックしたときの次の状態を返す。
// 同じ列は 自然な向き → 逆順 → 解除 (= 既定の並びに戻る) の 3 段階で切り替わる
const nextSortState = (column, sortKey, sortDir) => {
  if (sortKey !== column.key) {
    return { sortKey: column.key, sortDir: column.dir };
  }
  if (sortDir === column.dir) {
    return { sortKey: column.key, sortDir: column.dir === "asc" ? "desc" : "asc" };
  }
  return { sortKey: null, sortDir: "desc" };
};

// 見出しに出す説明 (次にクリックすると何が起きるか)
const sortHint = (column, sortKey, sortDir) => {
  if (!column.key) return "";
  if (sortKey !== column.key) return `${column.title}で並べ替え`;
  return sortDir === column.dir
    ? "クリックで逆順"
    : "クリックで並べ替えを解除";
};

// 列 1 つ分の比較関数を作る (空欄は常に末尾)
const makeComparator = (column, direction) => (rowA, rowB) => {
  let a = rowA[column.key];
  let b = rowB[column.key];
  if (column.type === "number") {
    a = toNumber(a);
    b = toNumber(b);
  }
  const aBlank = column.type === "number" ? a === null : isBlank(a);
  const bBlank = column.type === "number" ? b === null : isBlank(b);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;
  const result = compareValues(a, b, column.type);
  return direction === "asc" ? result : -result;
};

const Row = memo(function Row({ customer, status, onEdit }) {
  const ms = customer.member_status || "none";
  const ml = MEMBER_LABELS[ms] || MEMBER_LABELS.none;
  return (
    <div
      className={`grid-row ${
        status === "ongoing"
          ? "is-today-ongoing"
          : status === "cancel"
          ? "is-today-cancel"
          : ""
      } ${ms === "joined" ? "row-joined" : ""}`}
    >
      <div className="cell id">#{customer.id}</div>
      <div className="cell member">
        <span
          className={`member-badge ${ml.className}`}
          title={
            customer.member_since
              ? `${ml.ja} (${customer.member_since}〜)`
              : ml.ja
          }
        >
          <span className="dot" />
          <span className="lab">{ml.ja}</span>
        </span>
      </div>
      <div className="cell name">{customer.name || "—"}</div>
      <div className="cell age">{customer.age || "—"}</div>
      <div className="cell date">{customer.date || "—"}</div>
      <div className="cell studio">
        <span className="studio-chip">{customer.studio || "—"}</span>
      </div>
      <div className="cell lesson">{customer.lesson || "—"}</div>
      <div className="cell created">{customer.created_at || "—"}</div>
      <div className="cell mail" title={customer.mail}>
        {customer.mail || "—"}
      </div>
      <div className="cell zip">{customer.postcode || "—"}</div>
      <div className="cell tel">{customer.tel || "—"}</div>
      <div className="cell access" title={customer.access}>
        {customer.access || "—"}
      </div>
      <div className="cell msg" title={customer.message}>
        {customer.message || "—"}
      </div>
      <div className="cell center">
        <button className="edit-btn" onClick={() => onEdit(customer)}>
          編集
        </button>
      </div>
    </div>
  );
});

function FreeCustomerListPage() {
  useLoginCheck();

  const [customers, setCustomers] = useState([]);
  // 店舗で絞り込んでも曜日チップの人数を出せるよう、全店舗分を別に持つ
  const [allCustomers, setAllCustomers] = useState([]);
  const [weekdayStudios, setWeekdayStudios] = useState({});
  const [todayLessons, setTodayLessons] = useState([]);
  const [cancelLessons, setCancelLessons] = useState([]);
  const [cancelTrialIds, setCancelTrialIds] = useState([]);
  const [activeStudio, setActiveStudio] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [memberFilter, setMemberFilter] = useState("all"); // all / joined / existing / none
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(ROWS_PER_PAGE);
  // 並べ替え (sortKey が null のときは API が返した既定の並び = 予約日の新しい順)
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const cacheRef = useRef({});
  const abortRef = useRef(null);
  const panelBodyRef = useRef(null);

  const todayStr = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const todayDayKey = useMemo(() => WEEK[new Date().getDay()], []);

  // ----- データ取得 (キャッシュ + 中断対応) -----
  const fetchCustomersData = useCallback(async (studio) => {
    if (cacheRef.current[studio]) {
      setCustomers(cacheRef.current[studio]);
      setIsLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(
        `${apiUrl}/api/get_free_customers/?studio=${encodeURIComponent(studio)}`,
        { signal: controller.signal, credentials: "include" }
      );
      if (!res.ok) throw new Error("network");
      const data = await res.json();
      cacheRef.current[studio] = data;
      if (studio === "") setAllCustomers(data);
      setCustomers(data);
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("データの取得中にエラーが発生しました:", e);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomersData("");
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/get_weekday_studios/`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setWeekdayStudios(data.studios || {});
        setTodayLessons(data.today_lesson || []);
        setCancelLessons(data.cancel_lesson || []);
        setCancelTrialIds(data.cancel_trial_ids || []);
      } catch (e) {
        console.error("曜日別店舗の取得に失敗:", e);
      }
    })();
  }, [fetchCustomersData]);

  // ----- 検索 + 会員ステータスフィルタ -----
  const filtered = useMemo(() => {
    const k = searchKeyword.toLowerCase().trim();
    let result = customers;
    if (memberFilter !== "all") {
      result = result.filter((c) => (c.member_status || "none") === memberFilter);
    }
    if (k) {
      result = result.filter((c) =>
        [
          c.id?.toString(),
          c.name,
          c.studio,
          c.lesson,
          c.mail,
          c.tel,
          c.postcode,
          c.access,
          c.message,
          c.date,
          c.created_at,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(k))
      );
    }
    return result;
  }, [customers, searchKeyword, memberFilter]);

  // ----- 並べ替え (絞り込んだ結果の中で並べ替える) -----
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const column = COLUMNS.find((c) => c.key === sortKey);
    if (!column) return filtered;
    // 元配列は壊さずコピーしてから並べ替える
    return [...filtered].sort(makeComparator(column, sortDir));
  }, [filtered, sortKey, sortDir]);

  const handleSort = useCallback(
    (column) => {
      if (!column.key) return;
      const next = nextSortState(column, sortKey, sortDir);
      setSortKey(next.sortKey);
      setSortDir(next.sortDir);
    },
    [sortKey, sortDir]
  );

  // 今日以降の体験予約人数 (キーは "曜日|店舗")。
  // 曜日チップは予約日の曜日 × 店舗で作られているので同じ単位で数える
  const upcomingCounts = useMemo(() => {
    const counts = {};
    allCustomers.forEach((c) => {
      if (!c.date || c.date < todayStr) return;
      const [y, m, d] = c.date.split("-").map(Number);
      const key = `${WEEK[new Date(y, m - 1, d).getDay()]}|${c.studio}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [allCustomers, todayStr]);

  // 会員ステータス別集計
  const memberStats = useMemo(() => {
    const stats = { joined: 0, existing: 0, former: 0, none: 0 };
    customers.forEach((c) => {
      const s = c.member_status || "none";
      if (stats[s] !== undefined) stats[s] += 1;
    });
    const total = customers.length;
    const joinedRate = total > 0 ? (stats.joined / total) * 100 : 0;
    return { ...stats, total, joinedRate };
  }, [customers]);

  // 検索 / データ変更で表示件数リセット
  useEffect(() => {
    setVisibleCount(ROWS_PER_PAGE);
  }, [searchKeyword, customers]);

  // 並べ替えを変えたら表示件数を戻し、一覧を先頭までスクロールする
  useEffect(() => {
    setVisibleCount(ROWS_PER_PAGE);
    if (panelBodyRef.current) panelBodyRef.current.scrollTop = 0;
  }, [sortKey, sortDir]);

  const visibleCustomers = useMemo(
    () => sorted.slice(0, visibleCount),
    [sorted, visibleCount]
  );

  // ----- スクロール時に追加読み込み -----
  useEffect(() => {
    const el = panelBodyRef.current;
    if (!el) return;
    const onScroll = () => {
      if (
        el.scrollTop + el.clientHeight >= el.scrollHeight - 200 &&
        visibleCount < sorted.length
      ) {
        setVisibleCount((c) => Math.min(c + ROWS_PER_PAGE, sorted.length));
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [visibleCount, sorted.length]);

  const getRowStatus = useCallback(
    (customer) => {
      if (customer.date !== todayStr) return null;
      if (cancelTrialIds.includes(customer.id)) return "cancel";
      if (todayLessons.includes(customer.studio)) return "ongoing";
      return null;
    },
    [todayStr, cancelTrialIds, todayLessons]
  );

  // ----- 編集 (drawer) -----
  const handleOpenEdit = useCallback((customer) => {
    setSelectedCustomer(customer);
    setNewDate(customer.date || "");
  }, []);

  const closeDrawer = () => {
    setSelectedCustomer(null);
    setNewDate("");
  };

  // ESC で drawer close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    if (selectedCustomer) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedCustomer]);

  const handleDateChange = async () => {
    if (!selectedCustomer || !newDate) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/change_trial_date/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedCustomer.id, new_date: newDate }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      // cache を全studio分更新
      Object.keys(cacheRef.current).forEach((k) => {
        cacheRef.current[k] = cacheRef.current[k].map((c) =>
          c.id === updated.id ? updated : c
        );
      });
      if (cacheRef.current[""]) setAllCustomers(cacheRef.current[""]);
      setCustomers((prev) =>
        prev.map((c) => (c.id === updated.id ? updated : c))
      );
      alert(`${selectedCustomer.name}様の予約日を変更しました`);
      closeDrawer();
    } catch (e) {
      console.error("予約日変更失敗:", e);
      alert("変更に失敗しました");
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;
    if (!window.confirm(`${selectedCustomer.name}様の予約をキャンセルしますか？`)) {
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/cancel_trial_customer/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedCustomer.id }),
      });
      if (!res.ok) throw new Error();
      await res.json();
      Object.keys(cacheRef.current).forEach((k) => {
        cacheRef.current[k] = cacheRef.current[k].filter(
          (c) => c.id !== selectedCustomer.id
        );
      });
      if (cacheRef.current[""]) setAllCustomers(cacheRef.current[""]);
      setCustomers((prev) => prev.filter((c) => c.id !== selectedCustomer.id));
      alert(`${selectedCustomer.name}様の予約をキャンセルしました`);
      closeDrawer();
    } catch (e) {
      console.error("削除失敗:", e);
      alert("削除に失敗しました");
    }
  };

  return (
    <div className="free-customer-list-page">
      {/* header */}
      <div className="page-header">
        <div>
          <p className="eyebrow">Trial Reservations</p>
          <div className="title-row">
            <h1>体験申込者一覧</h1>
            <span className="count-pill">
              <span className="num">{sorted.length}</span>
              <span>件</span>
            </span>
          </div>
        </div>
        <div className="search-bar">
          <Icon name="search" />
          <input
            type="text"
            placeholder="名前 / スタジオ / 電話 / メール 等"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </div>
      </div>

      {/* 会員ステータス サマリー + フィルタ */}
      <div className="member-stats">
        <div className="ms-summary">
          <div className="ms-rate">
            <span className="rate-num">
              {memberStats.joinedRate.toFixed(1)}
            </span>
            <span className="rate-pct">%</span>
            <span className="rate-lab">入会率</span>
          </div>
          <div className="ms-counts">
            <span className="ms-chip ms-joined">
              <span className="dot" />
              入会 <strong>{memberStats.joined}</strong>
            </span>
            <span className="ms-chip ms-existing">
              <span className="dot" />
              既会員 <strong>{memberStats.existing}</strong>
            </span>
            <span className="ms-chip ms-former">
              <span className="dot" />
              退会済 <strong>{memberStats.former}</strong>
            </span>
            <span className="ms-chip ms-none">
              <span className="dot" />
              未入会 <strong>{memberStats.none}</strong>
            </span>
          </div>
        </div>
        <div className="ms-filter">
          {[
            { key: "all", label: "すべて" },
            { key: "joined", label: "入会" },
            { key: "existing", label: "既会員" },
            { key: "none", label: "未入会" },
            { key: "former", label: "退会済" },
          ].map((opt) => (
            <button
              key={opt.key}
              className={`mf-chip ${memberFilter === opt.key ? "active" : ""}`}
              onClick={() => setMemberFilter(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* day filter */}
      <div className="day-filter">
        <button
          className={`all-btn ${!activeStudio ? "active" : ""}`}
          onClick={() => {
            setActiveStudio("");
            fetchCustomersData("");
          }}
        >
          <span className="lab-en">All</span>
          <span className="lab-ja">すべて</span>
        </button>
        {WEEK.map((day) => {
          const studios = weekdayStudios[day];
          if (!studios || studios.length === 0) return null;
          const isToday = day === todayDayKey;
          return (
            <div
              key={day}
              className={`day-col ${isToday ? "is-today" : ""}`}
            >
              <div className="day-head">
                <span className="day-en">{WEEK_EN[WEEK.indexOf(day)]}</span>
                <span className="day-ja">{day}</span>
              </div>
              <div className="day-chips">
                {studios.map((studio) => {
                  const isOngoing =
                    isToday && todayLessons.includes(studio);
                  const isCancel =
                    isToday && cancelLessons.includes(studio);
                  const count = upcomingCounts[`${day}|${studio}`] || 0;
                  return (
                    <button
                      key={`${day}-${studio}`}
                      className={`chip ${
                        activeStudio === studio ? "active" : ""
                      } ${isOngoing ? "ongoing" : ""} ${
                        isCancel ? "cancel" : ""
                      }`}
                      onClick={() => {
                        setActiveStudio(studio);
                        fetchCustomersData(studio);
                      }}
                      title={`${studio} (今日以降の体験予約 ${count}人)`}
                    >
                      <span className="chip-name">{studio}</span>
                      {count > 0 && (
                        <span className="chip-count">({count})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* list panel */}
      <div className="panel">
        <div className="panel-header">
          <div className="section-title">
            <span className="ja">
              {activeStudio ? `${activeStudio} の体験予約` : "全体験予約"}
            </span>
          </div>
          <span className="panel-meta">
            {visibleCustomers.length} / {sorted.length}
          </span>
        </div>
        <div className="panel-body" ref={panelBodyRef}>
          <div className="fc-grid">
            <div className="grid-row grid-head">
              {COLUMNS.map((col) => {
                const isSorted = col.key && sortKey === col.key;
                return (
                  <div
                    key={col.key || col.title}
                    className={`cell ${col.className || ""} ${
                      col.key ? "sortable" : ""
                    } ${isSorted ? "is-sorted" : ""}`}
                    onClick={() => handleSort(col)}
                    title={sortHint(col, sortKey, sortDir)}
                  >
                    {col.title}
                    {isSorted && (
                      <span className="sort-ind">
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {isLoading && customers.length === 0 ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div className="skeleton-row" key={i}>
                  {Array.from({ length: 14 }).map((_, j) => (
                    <span className="bar" key={j} />
                  ))}
                </div>
              ))
            ) : sorted.length === 0 ? (
              <div className="empty-state">
                <p className="ja">該当する体験予約はありません</p>
                <p className="en">No matching reservations</p>
              </div>
            ) : (
              <>
                {visibleCustomers.map((c) => (
                  <Row
                    key={c.id}
                    customer={c}
                    status={getRowStatus(c)}
                    onEdit={handleOpenEdit}
                  />
                ))}
                {visibleCount < sorted.length && (
                  <div className="load-more">
                    <button
                      onClick={() =>
                        setVisibleCount((c) =>
                          Math.min(c + ROWS_PER_PAGE, sorted.length)
                        )
                      }
                    >
                      さらに表示 (+{Math.min(ROWS_PER_PAGE, sorted.length - visibleCount)})
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* drawer overlay */}
      <div
        className={`drawer-overlay ${selectedCustomer ? "open" : ""}`}
        onClick={closeDrawer}
      />

      {/* drawer */}
      <aside className={`fc-drawer ${selectedCustomer ? "open" : ""}`}>
        <button
          className="drawer-close"
          onClick={closeDrawer}
          aria-label="close"
        >
          <Icon name="close" />
        </button>
        {selectedCustomer && (
          <>
            <div className="drawer-hero">
              <p className="eyebrow">Edit Reservation</p>
              <h2 className="name">{selectedCustomer.name || "—"}</h2>
              <p className="lesson-line">
                {selectedCustomer.studio} / {selectedCustomer.lesson}
              </p>
              <div className="change-row">
                <div className="col">
                  <p className="lab">From</p>
                  <p className="val">{selectedCustomer.date || "—"}</p>
                </div>
                <div className="arrow">→</div>
                <div className="col">
                  <p className="lab">To</p>
                  <p className="val new-val">{newDate || "—"}</p>
                </div>
              </div>
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">日時変更</span>
              </p>
              <div className="field">
                <label>新しい予約日</label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </div>
            </div>

            <div className="drawer-section">
              <p className="section-title">
                <span className="ja">基本情報</span>
              </p>
              <dl className="info-grid">
                <dt>Mail</dt>
                <dd>{selectedCustomer.mail || "—"}</dd>
                <dt>Tel</dt>
                <dd className="num">{selectedCustomer.tel || "—"}</dd>
                <dt>年齢</dt>
                <dd className="num">{selectedCustomer.age || "—"}</dd>
                <dt>郵便番号</dt>
                <dd className="num">{selectedCustomer.postcode || "—"}</dd>
                <dt>交通</dt>
                <dd>{selectedCustomer.access || "—"}</dd>
                <dt>申込日</dt>
                <dd className="num">{selectedCustomer.created_at || "—"}</dd>
              </dl>
            </div>

            {selectedCustomer.message && (
              <div className="drawer-section">
                <p className="section-title">
                  <span className="ja">メッセージ</span>
                </p>
                <div className="msg-block">{selectedCustomer.message}</div>
              </div>
            )}

            <div className="drawer-actions">
              <button
                className="primary"
                onClick={handleDateChange}
                disabled={!newDate || newDate === selectedCustomer.date}
              >
                日時変更
              </button>
              <button className="danger" onClick={handleDelete}>
                予約キャンセル
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

export default FreeCustomerListPage;
